import React, { useState, useEffect, useRef } from 'react';
import { useStateValue } from './StateProvider';
import axios from 'axios';
import configData from './Config';
import ReactToPrint from 'react-to-print';
import moment from 'moment';
import StatementAccount from './StatementAccount';
import Select from './Components/select.js';

import { getStaffAccounts } from './Utils/staff.js';
import { getPermission } from './Utils/permissions.js';

import Title from './Title';

import './Staff.css';

const basisTypes = [
    { id: "cash", name: "Cash", description: 'Use this when payments are made immediately and no supplier invoices are recorded.' },
    { id: "accrual", name: "Accrual", description: 'Use this when supplier invoices need to be tracked and paid later through accounts payable.' },
];

function Staff({ state, setState }) {
    const [{ project }, dispatch] = useStateValue();

    const [filteredstaff, setFilteredstaff] = useState([]);
    const [search, setSearch] = useState('');

    const context = {
        ...project,
        staff: filteredstaff,
    };

    const staffdata = getStaffAccounts(context);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const filtered = project.staff.filter((staffmember) =>
            staffmember.name.toLowerCase().includes(search.toLowerCase())
        );
        setFilteredstaff(filtered)
    }, [project, search]);

    const handleCloseModal = () => { setState(state => ({ ...state, modalopen: false, modalcontent: null, modaltype: null, modaltitle: null })) }

    const addButton = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <AddStaff />, modaltype: 'small', modaltitle: 'Add Staff' }));
    }

    const editButton = (staff) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <EditStaff staff={staff} />, modaltype: 'small', modaltitle: 'Edit Staff' }));
    }

    const removeButton = (staff) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <RemoveStaff staff={staff} />, modaltype: 'small', modaltitle: 'Remove Staff' }));
    }

    const viewButton = (staff) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ViewStaff staff={staff} />, modaltype: 'small', modaltitle: 'View Staff' }));
    }

    const handleStatement = (type, account) => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <StatementAccount state={state} setState={setState} type={type} account={account} />, modaltype: 'large', modaltitle: 'Statement of Account' }));
    }

    const exportPage = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ExportPage />, modaltype: 'small', modaltitle: 'Export' }));
    }

    const importPage = () => {
        setState(state => ({ ...state, modalopen: true, modalcontent: <ImportPage />, modaltype: 'small', modaltitle: 'Export' }));
    }

    function ExportPage() {
        const [result, setResult] = useState('');

        useEffect(() => {
            if (!project || !project.staff || project.staff.length === 0) {
                setResult('No data to export');
                return;
            }

            const importedContent = JSON.stringify(project.staff, null, 2);

            navigator.clipboard.writeText(importedContent)
                .then(() => setResult('Data copied to clipboard'))
                .catch(err => setResult('Error copying data to clipboard: ', err));
        }, []);

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            {result}
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function ImportPage() {
        const [importText, setImportText] = useState('');
        const [newEntries, setNewEntries] = useState([]);
        const [importType, setImporttype] = useState('');

        const [loading, setLoading] = useState(false);
        const [result, setResult] = useState('');

        useEffect(() => {
            if (!importText) {
                setResult('');
                setNewEntries([]);
                return;
            }

            let isJSON = false;
            let data = [];

            try {
                const jsonData = JSON.parse(importText);
                isJSON = true;
                data = jsonData;
            } catch {
                isJSON = false;
            }

            if (isJSON) {
                setImporttype('json');
                const requiredAttributes = ["id", "name", "liabilityaccountid", "basis"];
                const isValidData = data.every(entry =>
                    requiredAttributes.every(attr => entry.hasOwnProperty(attr))
                );

                if (isValidData) {
                    const newEntries = data.filter(entry => {
                        return !project.suppliers.some(ac => ac.id === entry.id);
                    });

                    if (newEntries.length > 0) {
                        setNewEntries(newEntries);
                        setResult('Data is valid. New entries found: ' + newEntries.length);
                    }
                    else {
                        setResult(data.length + " entries found. No new data.");
                    }
                }
                else {
                    setResult('Invalid data: Missing required attributes.');
                }
            }

            if (!isJSON) {
                setResult('Invalid format');
            }
        }, [importText]);

        const handleImport = () => {
            console.log('Trying to import data');

            setResult('');

            const hasPermission = getPermission(
                project.projectuserid,
                project.users,
                state.user.userid,
                'Staff',
                'add',
                project.archived
            );
            if (hasPermission.code === 0) {
                setResult(hasPermission.data);
                return;
            }

            if (newEntries.length === 0) {
                setResult('No new entries to import');
                return;
            }

            setLoading(true);

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                staff: newEntries,
                type: importType
            }

            axios.post(configData.CONTROLLERURL + configData.IMPORTSTAFF, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Import data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        dispatch({
                            type: 'IMPORT_STAFF',
                            staff: newEntries
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data);
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
            })

            setResult('Import successful');
        };

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <textarea
                                className='modal_textarea'
                                value={importText}
                                onChange={e => setImportText(e.target.value)}
                                placeholder="Paste your import data here..."
                                rows={10}
                                cols={50}
                                style={{ resize: 'vertical' }}
                            />
                        </div>
                    </div>
                </div>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleImport} disabled={loading}>{loading ? 'Loading...' : 'Import'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function AddStaff() {
        const [name, setName] = useState('');
        const [liabilityaccountid, setLiabilityaccountid] = useState('');
        const [basis, setBasis] = useState('accrual');

        const [result, setResult] = useState(null);
        const [loading, setLoading] = useState(false);

        const addStaff = () => {
            console.log('Trying to add staff');

            setResult('');

            const hasPermission = getPermission(
                project.projectuserid,
                project.users,
                state.user.userid,
                'Staff',
                'add',
                project.archived
            );
            if (hasPermission.code === 0) {
                setResult(hasPermission.data);
                return;
            }

            if (!name) {
                setResult('Missing data');
                return;
            }

            setLoading(true);

            const trimmedName = name.trim();

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                name: trimmedName,
                liabilityaccountid: liabilityaccountid,
                basis: basis
            }

            axios.post(configData.CONTROLLERURL + configData.ADDSTAFFMEMBER, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Add staff data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        data.id = res.data.id;
                        dispatch({
                            type: 'ADD_STAFFMEMBER',
                            staffmember: data
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data)
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
            });
        }

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Name</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="text"
                                placeholder="Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Liability Account</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.liabilityaccounts} itemid={liabilityaccountid} setItemid={setLiabilityaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Basis</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={basisTypes} itemid={basis} setItemid={setBasis} />
                        </div>
                    </div>
                </div>
                <div className="modal_actions">
                    {result && <div className='modal_result'>{result}</div>}
                    <div className="modal_buttons">
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => addStaff()} disabled={loading}>{loading ? 'Loading...' : 'Save'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function EditStaff({ staff }) {
        const [id, setId] = useState('');
        const [name, setName] = useState([]);
        const [liabilityaccountid, setLiabilityaccountid] = useState('');
        const [basis, setBasis] = useState('');

        const [result, setResult] = useState(null);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            setId(staff.id)
            setName(staff.name)
            setLiabilityaccountid(staff.liabilityaccountid)
            setBasis(staff.basis)
        }, [staff]);

        const updateStaff = () => {
            console.log('Trying to update staff');

            setResult('');

            const hasPermission = getPermission(
                project.projectuserid,
                project.users,
                state.user.userid,
                'Staff',
                'update',
                project.archived
            );
            if (hasPermission.code === 0) {
                setResult(hasPermission.data);
                return;
            }

            if (!name) {
                setResult('Missing data');
                return;
            }

            const trimmedName = name.trim();

            setLoading(true);

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                id: id,
                name: trimmedName,
                liabilityaccountid: liabilityaccountid,
                basis: basis
            }

            axios.post(configData.CONTROLLERURL + configData.UPDATESTAFFMEMBER, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Update staff data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        dispatch({
                            type: 'UPDATE_STAFFMEMBER',
                            staffmember: data
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data)
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
            });
        }

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Name</div>
                        </div>
                        <div className='modal_rowsection'>
                            <input
                                className='modal_input'
                                type="text"
                                placeholder="Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Liability Account</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={project.liabilityaccounts} itemid={liabilityaccountid} setItemid={setLiabilityaccountid} />
                        </div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Basis</div>
                        </div>
                        <div className='modal_rowsection'>
                            <Select data={basisTypes} itemid={basis} setItemid={setBasis} />
                        </div>
                    </div>
                </div>
                <div className="modal_actions">
                    {result && <div className='modal_result'>{result}</div>}
                    <div className="modal_buttons">
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => updateStaff()} disabled={loading}>{loading ? 'Loading...' : 'Update'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => removeButton(staff)}>Remove</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function RemoveStaff({ staff }) {
        const [id, setId] = useState('');

        const [result, setResult] = useState(null);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            setId(staff.id)
            console.log(staff)
        }, [staff]);

        const removeStaff = () => {
            console.log('Trying to remove staff');

            setResult('');

            const hasPermission = getPermission(
                project.projectuserid,
                project.users,
                state.user.userid,
                'Staff',
                'remove',
                project.archived
            );
            if (hasPermission.code === 0) {
                setResult(hasPermission.data);
                return;
            }

            setLoading(true);

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                id: id
            }

            axios.post(configData.CONTROLLERURL + configData.REMOVESTAFFMEMBER, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Remove staff data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        dispatch({
                            type: 'REMOVE_STAFFMEMBER',
                            staffmemberid: id
                        });
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data)
                    }
                }
                else {
                    setResult('Error');
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
            });
        }

        return (
            <div className='modal_body'>
                <div className='modal_printable'>
                    <div className="modal_actions">
                        {result && <div className='modal_result'>{result}</div>}
                        <div className="modal_buttons">
                            <div className='modal_buttoncontainer'>
                                <button className="modal_button" onClick={() => removeStaff()} disabled={loading}>{loading ? 'Loading...' : 'Remove'}</button>
                            </div>
                            <div className='modal_buttoncontainer'>
                                <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function ViewStaff({ staff }) {
        const [id, setId] = useState('');
        const [name, setName] = useState('');

        const [result, setResult] = useState('');
        const [loading, setLoading] = useState(false);

        const printRef = useRef();

        useEffect(() => {
            setId(staff.id);
            setName(staff.name);
        }, [staff]);

        return (
            <div className='modal_body'>
                <div className='modal_printable' ref={printRef}>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Project Name</div>
                        </div>
                        <div className='modal_rowsection'>{project.name}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>ID</div>
                        </div>
                        <div className='modal_rowsection'>{id}</div>
                    </div>
                    <div className='modal_row'>
                        <div className='modal_rowsection'>
                            <div className='modal_label'>Name</div>
                        </div>
                        <div className='modal_rowsection'>{name}</div>
                    </div>
                </div>
                <div className="modal_action">
                    <div className="modal_buttons">
                        <div className="modal_buttoncontainer">
                            <ReactToPrint
                                trigger={() => (<button className="modal_button">Print</button>)}
                                content={() => printRef.current}
                            />
                        </div>
                        <div className="modal_buttoncontainer">
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className='staff'>
            <Title text='Staff' />

            <div className='buttonscontainer'>
                <div className='buttonsection'>
                    <button className='button' onClick={() => addButton()}>Add Staff</button>
                </div>
                <div className='buttonsection'>
                    <input
                        className='input'
                        type="text"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <div className='buttonscontainer'>
                <div className='buttonsection'>
                    <button className='button' onClick={exportPage}>Export</button>
                </div>
                <div className='buttonsection'>
                    <button className='button' onClick={importPage}>Import</button>
                </div>
            </div>

            <table className='table'>
                <thead>
                    <tr>
                        <th></th>
                        <th></th>
                        <th>Name</th>
                        <th>Balance</th>
                    </tr>
                </thead>
                <tbody>
                    {
                        staffdata.map((staffmember, index) => (
                            <tr key={'staffmember' + index}>
                                <td><div className='table_button' onClick={() => editButton(staffmember)}>Edit</div></td>
                                <td><div className='table_button' onClick={() => viewButton(staffmember)}>View</div></td>
                                <td><div>{staffmember.name}</div></td>
                                <td>
                                    <div
                                        className='staff_balance'
                                        onClick={() =>
                                            handleStatement(
                                                'staff',
                                                {
                                                    id: staffmember.id,
                                                    name: staffmember.name,
                                                    from: Math.floor(new Date(new Date(project.startdate * 1000).setHours(0, 0, 0, 0)).getTime() / 1000),
                                                    to: Math.floor(new Date().setHours(23, 59, 59, 999) / 1000),
                                                    subaccountid: '',
                                                    currencyid: project.basecurrency
                                                }
                                            )}
                                    >
                                        {staffmember.currencysymbol + ' ' + staffmember.balance.toLocaleString('en-US')}
                                    </div>
                                </td>
                            </tr>
                        ))
                    }
                </tbody>
            </table>
        </div >
    );
}

export default Staff;